import { useState } from "react";
import { Filter, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";

interface SkillsFilterProps {
  skillsFilter: string[];
  setSkillsFilter: (skills: string[]) => void;
  allSkills: string[];
}

export const SkillsFilter = ({ 
  skillsFilter, 
  setSkillsFilter, 
  allSkills 
}: SkillsFilterProps) => {
  const [skillsMenuOpen, setSkillsMenuOpen] = useState(false);
  const [skillsSearchTerm, setSkillsSearchTerm] = useState("");
  const { t } = useTranslation();

  // Toggle skill selection
  const toggleSkill = (skill: string) => {
    if (skillsFilter.includes(skill)) {
      setSkillsFilter(skillsFilter.filter(s => s !== skill));
    } else {
      setSkillsFilter([...skillsFilter, skill]);
    }
  };

  // Filter skills based on search term - case insensitive
  const filteredSkills = allSkills.filter(skill => 
    skill.toLowerCase().includes(skillsSearchTerm.toLowerCase())
  );

  return (
    <DropdownMenu open={skillsMenuOpen} onOpenChange={setSkillsMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="flex items-center gap-2"
          aria-expanded={skillsMenuOpen}
          data-state={skillsMenuOpen ? "open" : "closed"}
        >
          <Filter className="h-4 w-4" />
          <span>{t("candidates.filters.skillsButton")}</span>
          {skillsFilter.length > 0 && (
            <span className="ml-1 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
              {skillsFilter.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-56 p-3 bg-white" 
        align="start"
        onInteractOutside={() => setSkillsMenuOpen(false)}
      >
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t("candidates.filters.skillsSearchPlaceholder")}
              value={skillsSearchTerm}
              onChange={(e) => setSkillsSearchTerm(e.target.value)}
              className="pl-8 text-sm h-8"
            />
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {filteredSkills.length === 0 ? (
              <p className="text-sm text-gray-500 py-1">{t("candidates.filters.noSkillsMatch")}</p>
            ) : (
              filteredSkills.map(skill => (
                <div key={skill} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`skill-${skill}`} 
                    checked={skillsFilter.includes(skill)}
                    onCheckedChange={() => toggleSkill(skill)}
                  />
                  <Label 
                    htmlFor={`skill-${skill}`}
                    className="text-sm font-normal cursor-pointer flex-1"
                  >
                    {skill}
                  </Label>
                </div>
              ))
            )}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
